import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { TypeAheadDropdown } from 'app/ComponentLibrary/Filter/TypeAheadDropdown/TypeAheadDropdown';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectTagTypes } from 'app/components/Candidate/CandidateProfile/Profile/CandidateDetails.selector';
import {
  mapTagTypeToLookupOption,
  shouldDisplayCandidateTag,
} from 'app/components/Candidate/CandidateProfile/CandidateHeader/TagsWrapper/tagTypeHelpers';
import { ClassicTags } from 'app/components/Candidate/CandidateProfile/CandidateHeader/TagsWrapper/CandidateTagHelper';
import _ from 'lodash';

export type TagsCandidateGridRow = {
  candidateId?: number | string;
  brandId?: number | string;
  candidate?: string;
};

export type TagsManagementBulkTagsPickerHandle = {
  open: () => void;
};

type Props = {
  selectedRows: TagsCandidateGridRow[];
  anchorRef?: React.RefObject<HTMLElement>;
  onApply?: (selectedTags: any[], selectedRows: TagsCandidateGridRow[]) => void;
};

const ANCHOR_ACCESSOR_ID = 'tags-management-bulk-update-tags-anchor';

/**
 * Manage-tags picker (same TypeAheadDropdown UX as candidate profile header).
 * Save/update API wiring is handled separately.
 */
export const TagsManagementBulkTagsPicker = forwardRef<TagsManagementBulkTagsPickerHandle, Props>(
  ({ selectedRows, anchorRef, onApply }, ref) => {
    const { t } = useTranslation();
    const tagTypes = useSelector(selectTagTypes);
    const [selectedTags, setSelectedTags] = useState<any[]>([]);
    const [options, setOptions] = useState<any[]>([]);

    const lookupOptions = useMemo(() => {
      if (!tagTypes?.length) {
        return [];
      }
      return tagTypes.map(mapTagTypeToLookupOption);
    }, [tagTypes]);

    const tagsOptions = useMemo(
      () => lookupOptions.filter(option => !option.isHidden && shouldDisplayCandidateTag(option)),
      [lookupOptions],
    );

    useEffect(() => {
      setOptions(tagsOptions);
      setSelectedTags([]);
    }, [tagsOptions, selectedRows]);

    useImperativeHandle(ref, () => ({
      open: () => {
        document.getElementById(ANCHOR_ACCESSOR_ID)?.click();
      },
    }));

    const setDisabledOptions = (values: any[]) => {
      const selectedInterestRatingItem = values?.find(
        item =>
          item.value === ClassicTags.VaMilExp ||
          item.value === ClassicTags.KaizerHot ||
          item.value === ClassicTags.KaizerWarm,
      );
      if (selectedInterestRatingItem) {
        const updatedOptions = _.cloneDeep(tagsOptions)?.map(opt => {
          if (
            (opt.value === ClassicTags.VaMilExp ||
              opt.value === ClassicTags.KaizerHot ||
              opt.value === ClassicTags.KaizerWarm) &&
            opt.value !== selectedInterestRatingItem.value
          ) {
            opt.disabled = true;
          } else {
            opt.disabled = false;
          }
          return opt;
        });
        setOptions(updatedOptions);
      } else {
        const resetOptions = _.cloneDeep(tagsOptions)?.map(opt => {
          opt.disabled = false;
          return opt;
        });
        setOptions(resetOptions);
      }
    };

    const onOptionsChange = (values: any[]) => {
      setSelectedTags(values);
      setDisabledOptions(values);
    };

    const handleApply = () => {
      onApply?.(selectedTags, selectedRows);
    };

    return (
      <div
        style={{
          position: 'absolute',
          width: 0,
          height: 0,
          overflow: 'hidden',
          opacity: 0,
          pointerEvents: 'none',
        }}
        aria-hidden
      >
        <TypeAheadDropdown
          name="tags-management-bulk-type-ahead"
          value={selectedTags}
          anchorRef={anchorRef}
          onChange={onOptionsChange}
          options={options ?? []}
          isMultiSelect
          anchorAccesorid={ANCHOR_ACCESSOR_ID}
          getOptionDisabled={option => option.disabled}
          getOptionTooltip={option => option.fullDescription?.trim() || null}
          apply={handleApply}
          placeholder={t('search.filter.search')}
        />
      </div>
    );
  },
);

TagsManagementBulkTagsPicker.displayName = 'TagsManagementBulkTagsPicker';
